/**
 * Automations domain — utility functions and shared types.
 *
 * Everything in this file is non-hook: domain entity types re-exported
 * from the generated client, plus the imperative helpers that sit outside
 * the generated client because they need behavior Kubb doesn't emit:
 *   - multipart create/update (file attachments via `FormData`)
 *   - cycle-validation calls invoked imperatively mid-form-submit
 */

import type { AutomationMultipartPayload } from 'models/automation'
import { clientInstance } from '../../utils/http-client'
import { baseAPICall } from '../baseAPICall'
import { validateAutomationUpdate as validateAutomationUpdateClient } from '../generated/clients/validateAutomationUpdate'
import { validateNewAutomation as validateNewAutomationClient } from '../generated/clients/validateNewAutomation'
import type { AutomationDetail } from '../generated/types/AutomationDetail'
import type { CycleValidationRequestRequest } from '../generated/types/CycleValidationRequestRequest'
import type { ValidateAutomationUpdateResponse } from '../generated/types/ValidateAutomationUpdateResponse'
import type { ValidateNewAutomationResponse } from '../generated/types/ValidateNewAutomationResponse'

// ── Domain entity type re-exports / aliases ─────────────────────────

export type { AutomationList as Automation } from '../generated/types/AutomationList'
export type { AutomationDetail } from '../generated/types/AutomationDetail'
export type { AutomationAttachment } from '../generated/types/AutomationAttachment'
export type { AutomationAction } from '../generated/types/AutomationAction'
export type { AutomationCondition } from '../generated/types/AutomationCondition'
export type { AutomationConditionGroup } from '../generated/types/AutomationConditionGroup'

// ── Multipart create/update helpers ─────────────────────────────────
//
// The automations create/update endpoints accept either JSON (the shape
// drf-spectacular documents) OR multipart/form-data when the automation
// carries file attachments. The generated client only knows the JSON
// path, so the multipart path is hand-built here: complex objects are
// serialized as JSON strings and files appended individually, matching
// what the backend's `_handle_multipart_create/update` expects.

/**
 * Build the `FormData` body for a multipart automation create/update.
 *
 * `condition_groups` / `actions` go in as JSON strings; only `File`
 * instances are appended as attachments (existing `AutomationAttachment`
 * objects are already persisted and skipped — the update path adds new
 * files without disturbing existing ones).
 */
function buildAutomationFormData(data: AutomationMultipartPayload): FormData {
  const formData = new FormData()

  formData.append('name', data.name)
  if (data.description) {
    formData.append('description', data.description)
  }
  formData.append('is_active', String(data.is_active))
  formData.append('condition_groups', JSON.stringify(data.condition_groups))
  formData.append('actions', JSON.stringify(data.actions))

  if (data.attachments) {
    data.attachments.forEach((file) => {
      if (file instanceof File) {
        formData.append('attachments', file)
      }
    })
  }

  return formData
}

/** Create an automation with file attachments (multipart/form-data). */
export async function createAutomationMultipart(
  data: AutomationMultipartPayload
): Promise<AutomationDetail> {
  const formData = buildAutomationFormData(data)
  return baseAPICall<AutomationDetail>(() =>
    clientInstance.post('automations/automations/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  )
}

/** Update an automation with file attachments (multipart/form-data). */
export async function updateAutomationMultipart(
  id: number,
  data: AutomationMultipartPayload
): Promise<AutomationDetail> {
  const formData = buildAutomationFormData(data)
  return baseAPICall<AutomationDetail>(() =>
    clientInstance.put(`automations/automations/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  )
}

// ── Cycle-validation imperative helpers ─────────────────────────────
//
// These are POST endpoints but used imperatively (pre-submit form
// validation), not as React Query mutations — they don't mutate server
// state, so there's nothing to invalidate. Thin wrappers over the
// generated client functions keep components importing only from
// `services/automations`.

/** Validate that a new automation won't introduce a cycle. */
export function validateNewAutomationCycles(
  companyId: number,
  data: CycleValidationRequestRequest
): Promise<ValidateNewAutomationResponse> {
  return validateNewAutomationClient({ company_id: companyId }, data)
}

/** Validate that updating an automation won't introduce a cycle. */
export function validateAutomationUpdateCycles(
  companyId: number,
  automationId: number,
  data: CycleValidationRequestRequest
): Promise<ValidateAutomationUpdateResponse> {
  return validateAutomationUpdateClient(
    { company_id: companyId, automation_id: automationId },
    data
  )
}
